import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useKycSubmission } from "@/hooks";
import { KycStatus, KycType, type KycSubmission } from "@/interfaces/kyc";
import { queryClient } from "@/services/queryClient";
import { makeRequest } from "@/utils/makeRequest";
import { useMutation } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  AlertTriangle,
  Eye,
  X,
  Check,
  CheckCircle2,
  FileText,
  Clock,
  Shield,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import Loader from "@/components/loader/Loader";
import { ErrorState } from "@/components/ErrorState";

type KycLevel = 1 | 2 | 3;
type DocumentGroups = Record<KycType, Record<KycLevel, string[]>>;
type KycField = (typeof fieldGroups)[KycType][number];
type KycFormData = Record<string, string | number | boolean | null | undefined>;

const rejectionOptions = [
  "Unclear photo",
  "Name mismatch",
  "Expired document",
  "Suspicious activity",
  "Document not valid",
  "Other",
];

const fieldGroups = {
  PERSONAL: [
    "nationality",
    "phoneNumber",
    "address",
    "city",
    "state",
    "postalCode",
    "country",
  ],
  BUSINESS: [
    "businessName",
    "registrationNumber",
    "businessType",
    "taxId",
    "website",
    "businessAddress",
    "city",
    "state",
    "country",
    "directorFullName",
    "directorId",
  ],
} as const;

const documentGroups: DocumentGroups = {
  PERSONAL: {
    1: ["GOVERNMENT_ID", "SELFIE_WITH_ID"],
    2: ["PROOF_OF_ADDRESS"],
    3: ["SOURCE_OF_FUNDS", "BANK_REFERENCE", "INCOME_PROOF"],
  },

  BUSINESS: {
    1: ["BUSINESS_REG_CERT", "DIRECTOR_ID", "DIRECTOR_SELFIE"],
    2: ["TAX_CLEARANCE", "BUSINESS_BANK_STATEMENT"],
    3: ["AUDITED_FINANCIALS", "TRADE_LICENSE"],
  },
};

const AdminComplianceDetails = () => {
  const { id } = useParams();

  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] =
    useState<KycSubmission | null>(null);

  const [requestNote, setRequestNote] = useState("");
  const [requiredDocuments, setRequiredDocuments] = useState<string[]>([]);
  const [requiredFields, setRequiredFields] = useState<string[]>([]);

  const [selectedDocument, setSelectedDocument] = useState<
    KycSubmission["documents"][number] | null
  >(null);

  const [rejectReason, setRejectReason] = useState("");
  const [rejectNotes, setRejectNotes] = useState("");

  const { data: kyc, isLoading, isError, refetch } = useKycSubmission(id!);
  const formData = kyc?.formData?.formData ?? {};

  console.log("KYC", kyc);

  useEffect(() => {
    if (!kyc) return;

    setSelectedSubmission(kyc);

    if (kyc.documents?.length > 0) {
      setSelectedDocument((prev) => prev ?? kyc.documents[0]);
    }
  }, [kyc]);

  const resetRequestModal = () => {
    setRequestNote("");
    setRequiredDocuments([]);
    setRequiredFields([]);
  };

  const canRequestInfo =
    selectedSubmission?.status === KycStatus.PENDING ||
    selectedSubmission?.status === KycStatus.UNDER_REVIEW;

  const getFields = (type: KycType): KycField[] => {
    return [...fieldGroups[type]]; // clone removes readonly issue
  };

  const relevantFields = selectedSubmission
    ? getFields(selectedSubmission.kycType as KycType)
    : [];

  const level = selectedSubmission?.level as KycLevel | undefined;
  const type = selectedSubmission?.kycType;

  const relevantDocumentGroups =
    selectedSubmission && type && level ? documentGroups[type][level] : [];

  console.log("KYC", kyc);
  console.log("SELECTED DOCUMENT", selectedDocument);

  const toggleDoc = (doc: string) => {
    setRequiredDocuments((prev) =>
      prev.includes(doc) ? prev.filter((d) => d !== doc) : [...prev, doc],
    );
  };

  const toggleField = (field: string) => {
    setRequiredFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field],
    );
  };

  const handleSendRequest = () => {
    if (!selectedSubmission || !requestNote.trim()) {
      toast.error("Please provide a message");
      return;
    }

    requestInfoMutation.mutate({
      id: selectedSubmission.id,
      note: requestNote,
      requiredDocuments,
      requiredFields,
    });
  };

  const handleRequestMoreInfo = () => {
    if (!kyc) return;

    setShowReviewModal(false);

    setRequiredDocuments([]);
    setRequiredFields([]);
    setRequestNote("");

    setShowRequestModal(true);
  };

  const handleReject = () => {
    if (!selectedSubmission || !rejectReason) return;

    const reason = rejectNotes
      ? `${rejectReason}: ${rejectNotes}`
      : rejectReason;

    rejectMutation.mutate({
      id: selectedSubmission.id,
      reason,
    });
  };

  const requestInfoMutation = useMutation({
    mutationFn: async ({
      id,
      note,
      requiredDocuments,
      requiredFields,
    }: {
      id: string;
      note: string;
      requiredDocuments?: string[];
      requiredFields?: string[];
    }) => {
      return await makeRequest.put(`/kyc-submission/admin/${id}/request-info`, {
        note,
        requiredDocuments,
        requiredFields,
      });
    },
    onSuccess: () => {
      toast.success("Request for more information sent");
      queryClient.invalidateQueries({ queryKey: ["kyc-submissions:all"] });
      setShowRequestModal(false);
      setShowReviewModal(false);
      resetRequestModal();
    },
    onError: () => toast.error("Failed to send request"),
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      return await makeRequest.put(`/kyc-submission/admin/${id}/reject`, {
        reason,
      });
    },
    onSuccess: () => {
      toast.success("KYC Rejected");
      queryClient.invalidateQueries({ queryKey: ["kyc-submissions:all"] });
      setShowRejectModal(false);
      setShowReviewModal(false);
    },
    onError: () => toast.error("Failed to reject KYC"),
  });

  const handleApprove = () => {
    if (!selectedSubmission) return;
    approveMutation.mutate({ id: selectedSubmission.id });
  };

  const approveMutation = useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) => {
      return await makeRequest.put(`/kyc-submission/admin/${id}/approve`, {
        note,
      });
    },
    onSuccess: () => {
      toast.success("KYC Approved successfully");
      queryClient.invalidateQueries({ queryKey: ["kyc-submissions:all"] });
      queryClient.invalidateQueries({ queryKey: ["kyc-submission", id] });
      setShowReviewModal(false);
    },
    onError: () => toast.error("Failed to approve KYC"),
  });
  const formatLabel = (value?: string) => {
    if (!value) return "-";

    return value
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getStatusBadge = (status: KycStatus) => {
    switch (status) {
      case KycStatus.PENDING:
        return "amber";

      case KycStatus.UNDER_REVIEW:
        return "blue";

      case KycStatus.NEEDS_MORE_INFO:
        return "orange";

      case KycStatus.APPROVED:
        return "emerald";

      case KycStatus.REJECTED:
        return "red";

      default:
        return "gray";
    }
  };

  const statusBadgeClass = {
    amber: "bg-amber-500/15 text-amber-600 border border-amber-500/30",
    blue: "bg-blue-500/15 text-blue-600 border border-blue-500/30",
    orange: "bg-orange-500/15 text-orange-600 border border-orange-500/30",
    emerald: "bg-emerald-500/15 text-emerald-600 border border-emerald-500/30",
    red: "bg-red-500/15 text-red-600 border border-red-500/30",
    gray: "bg-gray-500/15 text-gray-600 border border-gray-500/30",
  } as const;

  if (isLoading) {
    return <Loader type="bounce" />;
  }

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <div>
      <div className="mx-auto w-full max-w-7xl p-1 py-6 lg:px-8">
        <div>
          <div className="text-xl">KYC Application Review</div>

          <p className="text-sm text-muted-foreground">
            Verify applicant information and submitted documents
          </p>
        </div>

        <div className="space-y-6">
          {/* ================= SUMMARY ================= */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Verification Level */}
            <Card className="border shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Verification Level
                    </p>
                    <p className="mt-1 text-3xl font-bold tracking-tight">
                      Level {kyc?.level ?? "-"}
                    </p>
                  </div>

                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
                    <Shield className="h-5 w-5 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Attempts */}
            <Card className="border shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Attempts</p>

                    <p className="mt-1 text-3xl font-bold tracking-tight">
                      #{kyc?.attempt ?? 0}
                    </p>
                  </div>

                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-500/10">
                    <Clock className="h-5 w-5 text-blue-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Application Type */}
            <Card className="border shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm text-muted-foreground">
                      Application Type
                    </p>

                    <p className="mt-1 truncate text-xl font-semibold">
                      {kyc?.kycType ?? "-"}
                    </p>
                  </div>

                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-500/10">
                    <FileText className="h-5 w-5 text-emerald-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Status */}
            <Card className="border shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>

                    <div className="mt-2">
                      <Badge
                        className={
                          statusBadgeClass[
                            getStatusBadge(kyc?.status as KycStatus)
                          ]
                        }
                      >
                        {kyc?.status ?? "UNKNOWN"}
                      </Badge>
                    </div>
                  </div>

                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-purple-500/10">
                    <CheckCircle2 className="h-5 w-5 text-purple-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ================= USER ================= */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Applicant Profile</CardTitle>
            </CardHeader>

            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="h-20 w-20 rounded-full bg-primary/10 border flex items-center justify-center text-primary font-bold text-2xl">
                  {kyc?.user?.firstName?.[0]}
                  {kyc?.user?.lastName?.[0]}
                </div>

                <div className=" grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1 ">
                  <div>
                    <p className="text-xs text-muted-foreground">Name</p>
                    <p className="font-medium">
                      {kyc?.user?.firstName} {kyc?.user?.lastName}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p className="font-medium break-all">{kyc?.user?.email}</p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground">Phone</p>
                    <p>{kyc?.user?.phoneNumber || "-"}</p>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground">Country</p>
                    <p>{kyc?.user?.country?.name || "-"}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ================= BUSINESS DETAILS ================= */}
          {kyc?.kycType === "BUSINESS" && (
            <Card className="">
              <CardHeader>
                <CardTitle>Business Information</CardTitle>
              </CardHeader>

              <CardContent>
                <div className="grid md:grid-cols-3 gap-5">
                  {Object.entries(formData).map(([key, value]) => (
                    <div key={key} className="rounded-lg border p-4">
                      <p className="text-xs text-muted-foreground">
                        {formatLabel(key)}
                      </p>

                      <p className="font-medium mt-1 break-words">
                        {String(value)}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ================= DOCUMENTS ================= */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Submitted Documents</CardTitle>
              <CardDescription>
                {kyc?.documents?.length || 0}
                documents submitted
              </CardDescription>
            </CardHeader>

            <CardContent className="p-0 overflow-hidden">
              <div className="grid grid-cols-1 xl:grid-cols-[340px_minmax(0,1fr)]">
                {/* ================= LEFT PANEL ================= */}

                <aside className="border-b xl:border-b-0 xl:border-r bg-muted/20">
                  <div className="p-4 border-b">
                    <h3 className="font-semibold">Submitted Documents</h3>

                    <p className="text-xs text-muted-foreground mt-1">
                      Select a document to preview
                    </p>
                  </div>

                  <div className="max-h-[420px] xl:max-h-[750px] overflow-y-auto">
                    {kyc?.documents?.map((doc, index) => {
                      const active = selectedDocument?.id === doc.id;

                      return (
                        <button
                          key={doc.id}
                          type="button"
                          onClick={() => setSelectedDocument(doc)}
                          className={`w-full border-b p-4 text-left transition ${active ? "border-l-4 border-l-primary bg-primary/5" : "hover:bg-muted/60"}
`}
                        >
                          <div className="flex items-center gap-3">
                            {/* Thumbnail */}

                            <div className="h-16 w-20 sm:h-20 sm:w-24 shrink-0 rounded-lg border bg-background overflow-hidden flex items-center justify-center">
                              <img
                                src={doc?.documentUrl}
                                alt={doc?.documentType}
                                className="max-h-full max-w-full object-contain"
                              />
                            </div>

                            {/* Information */}

                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className="font-medium text-sm leading-tight break-words">
                                  {formatLabel(doc?.documentType)}
                                </p>

                                <span className="text-xs text-muted-foreground shrink-0">
                                  #{index + 1}
                                </span>
                              </div>

                              <Badge variant="outline" className="mt-2">
                                {doc?.status}
                              </Badge>

                              <p className="mt-2 truncate text-xs text-muted-foreground">
                                {doc.documentUrl.split("/").pop()}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </aside>

                {/* ================= RIGHT PANEL ================= */}
                <section className="min-w-0 flex flex-col">
                  <div className="border-b p-4 sm:p-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold break-words">
                        {formatLabel(selectedDocument?.documentType)}
                      </h3>

                      <p className="text-sm text-muted-foreground">
                        Review uploaded document
                      </p>
                    </div>

                    <Badge className="w-fit">{selectedDocument?.status}</Badge>
                  </div>

                  <div className="flex-1 p-3 sm:p-4 lg:p-6">
                    <div className="rounded-xl border bg-muted h-[260px] sm:h-[360px] md:h-[480px] lg:h-[600px] xl:h-[650px] flex items-center justify-center overflow-hidden p-3">
                      {selectedDocument ? (
                        <img
                          src={selectedDocument?.documentUrl}
                          alt={selectedDocument?.documentType}
                          className="  w-auto  h-auto max-w-full max-h-full object-contain select-none"
                        />
                      ) : (
                        <p className="text-muted-foreground">
                          Select a document
                        </p>
                      )}
                    </div>

                    <div className="mt-5 flex flex-col sm:flex-row sm:justify-end gap-3">
                      <Button
                        variant="outline"
                        asChild
                        className="w-full sm:w-auto"
                      >
                        <a
                          href={selectedDocument?.documentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Eye className="mr-2 h-4 w-4 shrink-0" />

                          <span className="truncate">View Full Resolution</span>
                        </a>
                      </Button>
                    </div>
                  </div>
                </section>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Verification Timeline</CardTitle>
            </CardHeader>

            <CardContent>
              <div className="space-y-5">
                <div className="flex justify-between">
                  <span className="text-gray-500">Submitted</span>

                  <span className="text-base font-medium">
                    {kyc?.createdAt &&
                      formatDistanceToNow(new Date(kyc?.createdAt), {
                        addSuffix: true,
                      })}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-500">Last Updated</span>

                  <span className="text-base font-medium">
                    {kyc?.updatedAt &&
                      formatDistanceToNow(new Date(kyc.updatedAt), {
                        addSuffix: true,
                      })}
                  </span>
                </div>

                {kyc?.reviewMeta?.approvedAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Approved</span>

                    <span className="text-base font-medium">
                      {formatDistanceToNow(
                        new Date(kyc?.reviewMeta?.approvedAt),
                        { addSuffix: true },
                      )}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ================= REVIEW HISTORY ================= */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Review Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-5">
                <div>
                  <p className="text-xs text-muted-foreground">Review Note</p>
                  <p className="text-[14px]  mt-1">{kyc?.reviewNote || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Reviewed By</p>
                  <p>
                    {`${kyc?.reviewedBy?.firstName} ${kyc?.reviewedBy?.lastName}` ||
                      "-"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Decision</p>
                  <Badge
                    className={
                      statusBadgeClass[getStatusBadge(kyc?.status as KycStatus)]
                    }
                  >
                    {kyc?.status}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ================= ACTIONS ================= */}
          <div className="sticky bottom-0 bg-background border-t pt-4 pb-2 flex flex-col sm:flex-row justify-end gap-3">
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={handleRequestMoreInfo}
              disabled={!canRequestInfo}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Request Info
            </Button>

            <Button
              variant="destructive"
              className="w-full sm:w-auto"
              onClick={() => setShowRejectModal(true)}
            >
              <X className="h-4 w-4 mr-2" />
              Reject
            </Button>

            <Button
              className=" w-full sm:w-auto bg-emerald-600  hover:bg-emerald-700"
              onClick={handleApprove}
              disabled={
                approveMutation.isPending || kyc?.status === KycStatus.APPROVED
              }
            >
              <Check className="h-4 w-4 mr-2" />

              {approveMutation.isPending
                ? "Approving..."
                : "Approve Application"}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject KYC Application</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Rejection Reason</label>
              <Select onValueChange={setRejectReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {rejectionOptions.map((reason) => (
                    <SelectItem key={reason} value={reason}>
                      {reason}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Additional Notes</label>
              <Textarea
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                placeholder="Add more details..."
                className="min-h-[100px]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowRejectModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={rejectMutation.isPending}
              >
                {rejectMutation.isPending
                  ? "Rejecting..."
                  : "Confirm Rejection"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request More Info Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent className="mx-auto w-full max-w-7xl px-3 sm:px-6 max-h-[95vh] overflow-y-auto rounded-xl p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Request Additional Information</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Specify exactly what the user needs to provide to continue
              verification.
            </p>
          </DialogHeader>

          <div className="space-y-6">
            {/* ================= REQUIRED DOCUMENTS ================= */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Required Documents</p>
                <span className="text-xs text-muted-foreground">
                  Click to toggle selection
                </span>
              </div>

              {relevantDocumentGroups?.length ? (
                <div className="p-3 space-y-2 border rounded-md">
                  <p className="text-xs text-muted-foreground font-medium">
                    Level {selectedSubmission?.level}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    {relevantDocumentGroups.map((doc) => {
                      const isActive = requiredDocuments.includes(doc);

                      return (
                        <button
                          key={doc}
                          type="button"
                          onClick={() => toggleDoc(doc)}
                          className={`text-xs px-2 py-1 rounded-full border transition flex items-center gap-1 ${isActive ? "bg-primary text-primary-foreground border-primary shadow-sm" : "bg-background text-muted-foreground border-border hover:bg-muted"}`}
                        >
                          {doc.replace(/_/g, " ")}
                          {isActive && <Check className="h-3 w-3" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No document requirements for this level.
                </p>
              )}
            </div>

            {/* ================= REQUIRED FIELDS ================= */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Required Fields</p>
                <span className="text-xs text-muted-foreground">
                  Click to toggle selection
                </span>
              </div>

              <div className="border rounded-lg bg-blue-50/30 dark:bg-blue-950/10 p-3">
                {relevantFields.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No field requirements configured for this level.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {relevantFields.map((field) => {
                      const isActive = requiredFields.includes(field);

                      return (
                        <button
                          key={field}
                          type="button"
                          onClick={() => toggleField(field)}
                          className={`text-xs px-3 py-1 rounded-full border transition flex items-center gap-1 ${isActive ? "bg-blue-600 text-white border-blue-600" : "bg-white dark:bg-background text-blue-600 border-blue-200 hover:bg-blue-50"} `}
                        >
                          <span>{field.replace(/([A-Z])/g, " $1")}</span>

                          {isActive && <Check className="h-3 w-3" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ================= MESSAGE ================= */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Message to User</p>

              <Textarea
                value={requestNote}
                onChange={(e) => setRequestNote(e.target.value)}
                placeholder="Explain what is missing or unclear..."
                className="min-h-[120px] resize-none"
              />
              <p className="text-xs text-muted-foreground">
                This message will be visible to the user in their KYC dashboard.
              </p>
            </div>

            {/* ================= ACTIONS ================= */}
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2 border-t">
              <Button
                variant="outline"
                onClick={() => setShowRequestModal(false)}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>

              <Button
                onClick={handleSendRequest}
                disabled={requestInfoMutation.isPending || !requestNote.trim()}
                className="w-full sm:w-auto"
              >
                {requestInfoMutation.isPending ? "Sending..." : "Send Request"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminComplianceDetails;
